import {
  Box,
  Button,
  Center,
  Flex,
  Grid,
  Heading,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { AuctionHouse } from '@zoralabs/zdk';
import makeBlockie from 'ethereum-blockies-base64';
import { BigNumberish, ethers } from 'ethers';
import { DateTime } from 'luxon';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import React, { FC, useEffect, useState } from 'react';
import useSWR from 'swr';
import { convertSecondsToDay } from '../lib/helpers';
import { SongMetadata } from '../lib/types';
import { SongADay, SongADay__factory } from '../types';
import { DAO_ADDRESS, RESERVE_PRICE, SONGADAY_CONTRACT_ADDRESS } from '../utils/constants';
import { getSongAttributeValue } from '../utils/helpers';
import { DEFAULT_NETWORK, NETWORKS } from '../web3/constants';
import fetchGraph from '../web3/fetchGraph';
import { formatAddress, formatToken, parseErrorMessage, parseTokenURI } from '../web3/helpers';
import { useContract } from '../web3/hooks';
import { useENS } from '../web3/hooks/useENS';
import { useWallet } from '../web3/WalletContext';
import AuctionSongCard from './AuctionSongCard';
import BidList from './BidList';
import TransactionWaitModal from './modals/TransactionWaitModal';

const maxBidsToDisplay = 3;
const SONG_BY_NUMBER = `
query SongByNumber($token: String) {
  reserveAuctions(
    first: 1,
    where: {token: $token}
  ) {
    id
    tokenId
    transactionHash
    approved
    duration
    expectedEndTimestamp
    tokenOwner {
      id
    }
    status
    currentBid {
      id
      transactionHash
      amount
      bidder {
        id
      }
      bidType
      createdAtTimestamp
    }
    previousBids {
      id
      transactionHash
      amount
      bidder {
        id
      }
      bidType
      createdAtTimestamp
    }
    approvedTimestamp
    createdAtTimestamp
    finalizedAtTimestamp
  }
}
`;

const LATEST_SONG = `
query Song($tokenContract: String, $ownerAddress: String) {
  reserveAuctions(
    first: 1,
    where: {tokenContract: $tokenContract, approved: true, tokenOwner: $ownerAddress},
    orderBy: createdAtTimestamp,
    orderDirection: desc
  ) {
    id
    tokenId
    transactionHash
    approved
    duration
    expectedEndTimestamp
    tokenOwner {
      id
    }
    status
    currentBid {
      id
      transactionHash
      amount
      bidder {
        id
      }
      bidType
      createdAtTimestamp
    }
    previousBids {
      id
      transactionHash
      amount
      bidder {
        id
      }
      bidType
      createdAtTimestamp
    }
    approvedTimestamp
    createdAtTimestamp
    finalizedAtTimestamp
  }
}
`;

type Bid = {
  id: string;
  transactionHash: string;
  amount: string;
  bidder: User;
  bidType: string;
  createdAtTimestamp: string;
};

type User = {
  id: string;
};

type Song = {
  id: string;
  tokenId: string;
  transactionHash: string;
  approved: boolean;
  duration: string;
  expectedEndTimestamp: string;
  tokenOwner: User;
  status: string;
  currentBid: Bid;
  previousBids: Bid[];
  approvedTimestamp: string;
  createdAtTimestamp: string;
  finalizedAtTimestamp: string;
};

type SongData = {
  reserveAuctions: Song[];
};

const fetchSongFromSubgraph = async (songNbr?: string, latest?: boolean) => {
  if (!latest && !songNbr) {
    return undefined;
  }
  if (latest) {
    const { data } = await fetchGraph<SongData, { tokenContract: string; ownerAddress: string }>(
      Number(DEFAULT_NETWORK),
      LATEST_SONG,
      {
        tokenContract: SONGADAY_CONTRACT_ADDRESS.toLowerCase(),
        ownerAddress: DAO_ADDRESS.toLowerCase(),
      },
    );

    return data.reserveAuctions[0];
  } else {
    const { data } = await fetchGraph<SongData, { token: string }>(
      Number(DEFAULT_NETWORK),
      SONG_BY_NUMBER,
      {
        token: `${SONGADAY_CONTRACT_ADDRESS.toLowerCase()}-${songNbr}`,
      },
    );
    return data.reserveAuctions[0];
  }
};

const Auction: React.FC<{ latest?: boolean }> = ({ latest }) => {
  const toast = useToast();
  const { isOpen: isTxOpen, onOpen: onTxOpen, onClose: onTxClose } = useDisclosure();
  const { isOpen: isHistoryOpen, onOpen: onHistoryOpen, onClose: onHistoryClose } = useDisclosure();
  const [pendingTxHash, setPendingTxHash] = useState<string | undefined>(undefined);
  const [bidValue, setBidValue] = useState<string>('');
  const router = useRouter();
  const { songNbr } = router.query;

  const [previousSong, setPreviousSong] = useState<Song>();
  const [nextSong, setNextSong] = useState<Song>();

  const [songMetadata, setSongMetadata] = useState<SongMetadata>();
  const [expired, setExpired] = useState<boolean>(false);
  const [finalised, setFinalised] = useState<boolean>(false);
  const [duration, setDuration] = useState<{
    days?: string;
    hours?: string;
    minutes?: string;
    years?: string;
    seconds?: string;
  }>({});

  const { contract: songContract } = useContract(SONGADAY_CONTRACT_ADDRESS, SongADay__factory, {
    useStaticProvider: true,
  });

  const { provider, chainId, address } = useWallet();

  const [auctionHouseContract, setAuctionHouseContract] = useState<AuctionHouse>();
  useEffect(() => {
    if (provider) {
      const auctionHouseContract = new AuctionHouse(provider?.getSigner(), Number(DEFAULT_NETWORK));
      setAuctionHouseContract(auctionHouseContract);
    }
  }, [provider]);

  const isValidBid = (bid: string) => {
    return (
      bid &&
      bid.toLowerCase() === bid.toUpperCase() &&
      Number(bid) >= calculateMinBid(currentBid?.amount)
    );
  };

  const createBid = async (auctionId: string, amount: BigNumberish) => {
    if (!address) {
      toast({
        status: 'error',
        title: 'An error occurred',
        description: 'Please connect to the wallet before placing the bid',
      });
      return;
    }

    if (chainId !== Number(DEFAULT_NETWORK)) {
      toast({
        status: 'error',
        title: 'An error occurred',
        description: `You are not connected to the ${NETWORKS[DEFAULT_NETWORK].name}`,
      });

      return;
    }

    try {
      onTxOpen();
      const bidTx = await auctionHouseContract?.createBid(auctionId, amount);
      setPendingTxHash(bidTx?.hash);
      await bidTx?.wait(1);
      toast({
        status: 'success',
        title: 'Bid placed',
        description: 'Your bid will show up here in a few minutes maximum. Thank you!',
      });
    } catch (error) {
      toast({
        status: 'error',
        title: 'An error occurred',
        description: parseErrorMessage(error) ?? 'Could not place the bid',
      });
    } finally {
      onTxClose();
      setPendingTxHash(undefined);
      setBidValue('');
    }
  };

  const settleAuction = async (auctionId: string) => {
    if (!address) {
      toast({
        status: 'error',
        title: 'An error occurred',
        description: 'Please connect to the wallet before settling the bid',
      });
      return;
    }

    if (chainId !== Number(DEFAULT_NETWORK)) {
      toast({
        status: 'error',
        title: 'An error occurred',
        description: `You are not connected to the ${NETWORKS[DEFAULT_NETWORK].name}`,
      });

      return;
    }

    try {
      onTxOpen();
      const bidTx = await auctionHouseContract?.endAuction(auctionId);
      setPendingTxHash(bidTx?.hash);
      await bidTx?.wait(2);
      toast({
        status: 'success',
        title: 'Auction Settled',
        description:
          'The winner will be transfered the song, and the auction will be closed. Please refresh the page after some time.',
      });
    } catch (error) {
      toast({
        status: 'error',
        title: 'An error occurred',
        description: parseErrorMessage(error) ?? 'Could not settle the auction',
      });
    } finally {
      onTxClose();
      setPendingTxHash(undefined);
    }
  };

  const fetchSong = async () => {
    try {
      const _song = await fetchSongFromSubgraph((songNbr as string) ?? '', latest);
      if (_song) {
        const tokenURI = await (songContract as SongADay)?.tokenURI(_song?.tokenId);
        const songMetadata = await fetchMetadata(tokenURI);
        setSongMetadata(songMetadata);
      }
      return _song;
    } catch (error) {
      console.log(error);
      throw new Error(error);
    }
  };

  //! I don't think we should get the entire data for these songs, but Im not sure how to find the next and prev songs yet - Beyondr
  const fetchPreviousSong = async (songNbr: number) => {
    // currently hardcoding two levels deep, ideally create a function and define how many times it should go deeper.
    try {
      const _song = await fetchSongFromSubgraph(songNbr.toString(), false);
      if (_song.tokenId) {
        setPreviousSong(_song);
      } else {
        const _song1 = await fetchSongFromSubgraph((songNbr - 1).toString(), false);
        if (_song1.tokenId) {
          setPreviousSong(_song1);
        } else {
          const _song2 = await fetchSongFromSubgraph((songNbr - 2).toString(), false);
          if (_song2.tokenId) {
            setPreviousSong(_song2);
          } else {
            setPreviousSong(undefined);
          }
        }
      }
    } catch (error) {
      setPreviousSong(undefined);
      return null;
    }
  };

  const fetchNextSong = async (songNbr: number) => {
    try {
      const _song = await fetchSongFromSubgraph(songNbr.toString(), false);
      if (_song.tokenId) {
        setNextSong(_song);
      } else {
        const _song1 = await fetchSongFromSubgraph((songNbr - 1).toString(), false);
        if (_song1.tokenId) {
          setNextSong(_song1);
        } else {
          const _song2 = await fetchSongFromSubgraph((songNbr - 2).toString(), false);
          if (_song2.tokenId) {
            setNextSong(_song2);
          } else {
            setNextSong(undefined);
          }
        }
      }
    } catch (error) {
      setNextSong(undefined);
      return null;
    }
  };

  const calculateMinBid = (amount) => {
    const oldBid = Number(formatToken(amount) ?? '0');
    return oldBid === 0 ? RESERVE_PRICE : oldBid * 1.05;
  };

  const { data, error } = useSWR(`song/${songNbr ?? ''}`, fetchSong, {
    refreshInterval: 15000,
  });
  if (!data && !error) console.log('loading...');
  if (error) console.error('swr error', error);
  const song = data;
  const loading = !data && !error && !song && !songMetadata;

  useEffect(() => {
    const calculateDuration = () => {
      const now = new Date().getTime(); // current datetime as milliseconds
      // The duration of the auction comes from the first bid
      const msDiff = song?.expectedEndTimestamp
        ? Number(song?.expectedEndTimestamp) * 1000 - now
        : (Number(song?.approvedTimestamp) + Number(song?.duration)) * 1000 - now;

      if (msDiff < 0 && !expired) {
        setExpired(true);
      }

      if (song?.finalizedAtTimestamp && !finalised) {
        setFinalised(true);
      }
      const duration = convertSecondsToDay(msDiff / 1000);
      setDuration(duration);
    };

    // Calculate first time
    calculateDuration();

    const interval = setInterval(() => {
      if (!finalised) {
        calculateDuration();
      }
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [song]);

  useEffect(() => {
    if (song?.tokenId) {
      fetchPreviousSong(Number(song.tokenId) - 1);
      fetchNextSong(Number(song.tokenId) + 1);
    }
  }, [song?.tokenId]);

  const fetchMetadata = async (tokenURI: string) => {
    try {
      const URI = parseTokenURI(tokenURI);
      const response = await fetch(URI);
      const songmeta = await response.json();
      return songmeta;
    } catch (e) {
      console.log('metaData fatch error', e);
    }
  };

  const date = DateTime.fromFormat(
    getSongAttributeValue(songMetadata?.attributes, 'Date') ?? '',
    'yyyy-MM-dd',
  );

  const subtitleDateString = date.toLocaleString(DateTime.DATE_MED);

  const currentBid = finalised
    ? song?.previousBids?.find((bid) => bid.bidType === 'Final')
    : song?.currentBid;

  const { ens, avatar, loading: ensLoading } = useENS({ address: currentBid?.bidder.id ?? '' });
  // eslint-disable-next-line react/display-name
  const WinningBidder: FC<{ currentBid: Bid }> = ({ currentBid }) => {
    const bidderLabel = ens ?? formatAddress(currentBid?.bidder.id);
    const bidderAvatar = avatar ?? makeBlockie(currentBid?.bidder.id ?? '');

    return (
      <>
        <Text fontSize={{ base: 'sm', md: 'md' }}>{finalised ? 'Winner' : 'Current Bidder'}</Text>
        <Flex align="center">
          {ensLoading && !avatar ? (
            <Spinner thickness="2px" speed="0.56s" emptyColor="teal.50" size="sm" color="teal" />
          ) : (
            <Image
              borderRadius="full"
              boxSize={{ base: '16px', md: '24px' }}
              src={bidderAvatar}
              fallbackSrc={makeBlockie(currentBid?.bidder.id ?? '')}
            />
          )}
          <Heading ml="2" as="h2" fontSize={{ base: 'xl', lg: '3xl' }} fontWeight="bold">
            {bidderLabel}
          </Heading>
        </Flex>
      </>
    );
  };

  return (
    <>
      {loading ? (
        <Flex h="8vh" justify="center" align="center" bgColor="brand.lightTeal">
          <Spinner thickness="4px" speed="0.56s" emptyColor="teal.50" size="lg" color="teal" />
          <Text pl={4}>Alright, let's find that song...</Text>
        </Flex>
      ) : error ? (
        <Flex
          h="8vh"
          justify="center"
          bgColor="brand.lightTeal"
          p={10}
          direction="column"
          align="center"
        >
          {latest ? (
            <Text fontSize="xl">An auction isn't running right now, try again later...</Text>
          ) : (
            <Text>Well, I couldn't find that song... </Text>
          )}
        </Flex>
      ) : (
        <Center bgColor="brand.lightTeal">
          <Grid
            templateRows={{ base: 'repeat(2, 1fr)', md: '1fr' }}
            templateColumns={{ base: '1fr', md: '1fr 1fr' }}
            gap={{ base: '4', sm: '8', lg: '16' }}
            px={{ base: 4, md: 8 }}
            py={{ base: 6, md: 8 }}
            maxWidth="container.xl"
          >
            <Box display="flex" justifyContent="center" alignItems="center">
              <AuctionSongCard song={songMetadata} />
            </Box>

            <Box maxWidth="xl">
              <Text fontSize="lg">
                Song #{song?.tokenId} / {subtitleDateString}
              </Text>
              <Flex
                alignItems={{ base: 'start', md: 'center' }}
                pt={1}
                direction={{ base: 'column', md: 'row' }}
              >
                <Heading
                  as="h1"
                  fontSize={{ base: '3xl', lg: '5xl' }}
                  fontWeight="regular"
                  letterSpacing="tight"
                >
                  {songMetadata?.name}
                </Heading>
                <Flex pl={{ base: 0, md: '6' }}>
                  {previousSong && (
                    <NextLink href={`/auction/${previousSong.tokenId}`} passHref>
                      <Link
                        _hover={{
                          textDecoration: 'none',
                        }}
                        isExternal
                      >
                        <Text fontSize="4xl">←</Text>
                      </Link>
                    </NextLink>
                  )}
                  {nextSong && (
                    <NextLink href={`/auction/${nextSong.tokenId}`} passHref>
                      <Link
                        _hover={{
                          textDecoration: 'none',
                        }}
                        isExternal
                      >
                        <Text fontSize="4xl" pl="2.5">
                          →
                        </Text>
                      </Link>
                    </NextLink>
                  )}
                </Flex>
              </Flex>

              <Stack spacing={{ base: '8', sm: '16' }} pt="6" direction="row">
                <Flex flexDir="column">
                  <Text fontSize={{ base: 'sm', md: 'md' }}>
                    {finalised ? 'Winning Bid' : 'Current Bid'}
                  </Text>
                  <Heading as="h2" fontSize={{ base: 'xl', lg: '3xl' }} fontWeight="bold">
                    Ξ {formatToken(currentBid?.amount) ?? '0'}
                  </Heading>
                </Flex>
                <Flex flexDir="column">
                  {expired ? (
                    <WinningBidder currentBid={currentBid} />
                  ) : (
                    <>
                      <Text fontSize={{ base: 'sm', md: 'md' }}>Ends In</Text>
                      <Heading as="h2" fontSize={{ base: 'xl', lg: '3xl' }} fontWeight="bold">
                        {duration.hours}h {duration.minutes}m {parseInt(duration.seconds)}s
                      </Heading>
                    </>
                  )}
                </Flex>
              </Stack>

              {finalised ? null : expired ? (
                <Flex pt="8">
                  <Button size="lg" onClick={() => settleAuction(song.id)}>
                    Settle Auction
                  </Button>
                </Flex>
              ) : (
                <>
                  <Text mt={6} fontSize="small" fontWeight="semibold">
                    Minimum bid:{' '}
                    {currentBid?.amount ? calculateMinBid(currentBid?.amount) : RESERVE_PRICE} ETH
                  </Text>
                  <Flex direction={{ base: 'column', md: 'row' }} gridGap={2}>
                    <InputGroup size="lg">
                      <InputRightElement pointerEvents="none">
                        <Text color="gray.400">ETH</Text>
                      </InputRightElement>
                      <Input
                        type="number"
                        min={
                          currentBid?.amount ? calculateMinBid(currentBid?.amount) : RESERVE_PRICE
                        }
                        step={0.01}
                        focusBorderColor="teal.500"
                        bgColor="white"
                        onChange={(e) => setBidValue(e.target.value)}
                        value={bidValue}
                      />
                    </InputGroup>
                    <Button
                      size="lg"
                      disabled={!isValidBid(bidValue)}
                      onClick={() => createBid(song.id, ethers.utils.parseEther(bidValue))}
                    >
                      Place bid ✋🏽
                    </Button>
                  </Flex>
                </>
              )}
              <TransactionWaitModal txHash={pendingTxHash} isOpen={isTxOpen} onClose={onTxClose} />

              <BidList
                currentBid={currentBid}
                previousBids={song?.previousBids}
                max={maxBidsToDisplay}
                opts={{ bgColor: 'blackAlpha.50' }}
              />
              {song?.previousBids?.length > maxBidsToDisplay && (
                <Box pt={4}>
                  <Link
                    mt="3"
                    fontWeight="medium"
                    fontSize="sm"
                    textDecoration="underline"
                    onClick={onHistoryOpen}
                  >
                    See all {song.previousBids.length} bids →
                  </Link>
                  <Modal
                    onClose={onHistoryClose}
                    isOpen={isHistoryOpen}
                    isCentered
                    scrollBehavior="inside"
                    motionPreset="scale"
                  >
                    <ModalOverlay />
                    <ModalContent>
                      <ModalHeader>Bid History</ModalHeader>
                      <ModalCloseButton />
                      <ModalBody>
                        <Heading fontSize="4xl">
                          Song {Number(song.tokenId).toLocaleString()}
                        </Heading>
                        <Text opacity={0.8} mt={2}>
                          {song.previousBids.length + 1} bids in total
                        </Text>
                        <BidList
                          currentBid={currentBid}
                          previousBids={song?.previousBids}
                          opts={{ bgColor: 'brand.lightTeal' }}
                        />
                      </ModalBody>
                      <ModalFooter>
                        <Button onClick={onHistoryClose}>Close</Button>
                      </ModalFooter>
                    </ModalContent>
                  </Modal>
                </Box>
              )}
            </Box>
          </Grid>
        </Center>
      )}
    </>
  );
};

export default Auction;
